import {
    Alert,
    Box,
    Button,
    Card,
    CircularProgress,
    Container,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { Link } from 'react-router-dom';
import {
    useDataProvider,
    useGetIdentity,
    useGetOne,
    useNotify,
    usePermissions,
    useRefresh,
} from 'react-admin';
import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { User } from '../../types/types';
import { IrmDataProvider } from '../../providers/types';
import { ApiAccessSection } from './ApiAccessSection';
import { MutedTopicsSection } from './MutedTopicsSection';

export const SettingsPage = () => {
    const { identity } = useGetIdentity();
    const { permissions } = usePermissions();
    const notify = useNotify();
    const refresh = useRefresh();
    const dp = useDataProvider<IrmDataProvider>();

    const { data, refetch } = useGetOne<User>(
        'users',
        { id: identity?.id != null ? String(identity.id) : '' },
        { enabled: Boolean(identity?.id) }
    );

    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (data) {
            setFirstName(data.first_name ?? '');
            setLastName(data.last_name ?? '');
        }
    }, [data]);

    const { mutate, isPending } = useMutation({
        mutationFn: async () => {
            const patch: { first_name?: string; last_name?: string } = {};
            const nextFirst = firstName.trim();
            const nextLast = lastName.trim();
            if (nextFirst !== (data?.first_name ?? '')) patch.first_name = nextFirst;
            if (nextLast !== (data?.last_name ?? '')) patch.last_name = nextLast;
            if (Object.keys(patch).length === 0) return { ok: true as const, noop: true };
            await dp.updateMyProfile(patch);
            return { ok: true as const, noop: false };
        },
        onSuccess: result => {
            setError(null);
            if ('noop' in result && result.noop) {
                notify('Nothing to save.');
                return;
            }
            notify('Profile updated.');
            refetch();
            refresh();
        },
        onError: (e: any) => {
            const slug: string =
                (typeof e?.details === 'string' && e.details) ||
                e?.details?.code ||
                e?.message ||
                '';
            setError(humanizeProfileError(slug));
        },
    });

    if (!identity || !data) return null;

    const dirty =
        firstName.trim() !== (data.first_name ?? '') ||
        lastName.trim() !== (data.last_name ?? '');

    return (
        <Container maxWidth="sm" sx={{ mt: 4 }}>
            <Card sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Your profile
                </Typography>
                <Stack gap={2}>
                    <TextField
                        label="First name"
                        value={firstName}
                        onChange={e => setFirstName(e.target.value)}
                        inputProps={{ maxLength: 80 }}
                        fullWidth
                    />
                    <TextField
                        label="Last name"
                        value={lastName}
                        onChange={e => setLastName(e.target.value)}
                        inputProps={{ maxLength: 80 }}
                        fullWidth
                    />
                    <Field label="Email" value={data.email} />
                    <Field label="Role" value={data.role} />
                    <Field label="Status" value={data.status} />
                </Stack>
                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}
                <Box sx={{ mt: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Button
                        variant="contained"
                        disabled={!dirty || isPending}
                        onClick={() => mutate()}
                        startIcon={
                            isPending ? <CircularProgress size={16} /> : undefined
                        }
                    >
                        Save
                    </Button>
                    <Typography variant="body2" color="text.secondary">
                        Email, role, and status are managed by an administrator.
                    </Typography>
                </Box>
                {permissions === 'admin' && (
                    <Button
                        component={Link}
                        to={`/admin/users/${identity.id}`}
                        variant="outlined"
                        sx={{ mt: 2 }}
                    >
                        Edit my profile (admin)
                    </Button>
                )}
            </Card>
            <ApiAccessSection
                user={{
                    id: String(data.id),
                    email: data.email,
                    role: data.role,
                    status: data.status,
                    allowed_features: data.allowed_features,
                }}
            />
            <MutedTopicsSection />
        </Container>
    );
};

const PROFILE_ERROR_MESSAGES: Record<string, string> = {
    empty_first_name: 'First name cannot be empty.',
    empty_last_name: 'Last name cannot be empty.',
    invalid_first_name: 'First name must be text.',
    invalid_last_name: 'Last name must be text.',
    too_long_first_name: 'First name is too long (max 80 characters).',
    too_long_last_name: 'Last name is too long (max 80 characters).',
    not_approved: 'Your account is not active. Contact your administrator.',
    sign_in_required: 'Please sign in again.',
    no_user: 'Your profile could not be found.',
    empty_patch: 'No changes to save.',
    invalid_patch: 'Invalid request.',
};

function humanizeProfileError(slug: string): string {
    if (!slug) return 'Could not save your profile. Try again.';
    if (PROFILE_ERROR_MESSAGES[slug]) return PROFILE_ERROR_MESSAGES[slug];
    if (slug.startsWith('unknown_field')) return 'Unsupported field in this form.';
    return 'Could not save your profile. Try again.';
}

const Field = ({ label, value }: { label: string; value?: string }) => (
    <Stack direction="row" justifyContent="space-between">
        <Typography variant="body2" color="text.secondary">
            {label}
        </Typography>
        <Typography variant="body2">{value || '—'}</Typography>
    </Stack>
);

SettingsPage.path = '/settings';
